import { ApiError } from "@/lib/apiClient";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Wholesaler,
  WholesalerProduct,
  WholesalerCollection,
  FetchWholesalersOptions,
  SearchWholesalersOptions,
  FetchWholesalerProductsOptions,
  SearchWholesalerProductsOptions,
  FetchWholesalerCollectionsOptions,
  SearchWholesalerCollectionsOptions,
  ImportProductsRequest,
  ImportCollectionsRequest,
  UnmapProductsRequest,
  UnmapCollectionsRequest,
} from "@/services/wholesalerService";
import { pagination, FormState } from "@/types";

interface WholesalersState {
  // Wholesaler list state
  items: Wholesaler[];
  loading: boolean;
  error: ApiError | null;
  pagination: pagination;

  // Single wholesaler state
  currentWholesaler: Wholesaler | null;
  wholesalerLoading: boolean;
  wholesalerError: ApiError | null;

  // Search state
  searchResults: Wholesaler[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: pagination;

  // Wholesaler Products
  wholesalerProducts: WholesalerProduct[];
  productsLoading: boolean;
  productsError: ApiError | null;
  productsPagination: pagination;

  // Wholesaler Product Search
  productSearchResults: WholesalerProduct[];
  productSearchLoading: boolean;
  productSearchError: ApiError | null;
  productSearchPagination: pagination;

  // Wholesaler Collections
  wholesalerCollections: WholesalerCollection[];
  collectionsLoading: boolean;
  collectionsError: ApiError | null;
  collectionsPagination: pagination;

  // Wholesaler Collection Search
  collectionSearchResults: WholesalerCollection[];
  collectionSearchLoading: boolean;
  collectionSearchError: ApiError | null;
  collectionSearchPagination: pagination;

  // Import/Export operations
  importLoading: boolean;
  importError: ApiError | null;
  importSuccess: boolean;

  // Product/Collection selection for bulk operations
  selectedProducts: string[];
  selectedCollections: string[];

  // Form state for various operations
  formState: FormState;
}

const initialState: WholesalersState = {
  items: [],
  loading: false,
  error: null,
  pagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  currentWholesaler: null,
  wholesalerLoading: false,
  wholesalerError: null,
  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  wholesalerProducts: [],
  productsLoading: false,
  productsError: null,
  productsPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  productSearchResults: [],
  productSearchLoading: false,
  productSearchError: null,
  productSearchPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  wholesalerCollections: [],
  collectionsLoading: false,
  collectionsError: null,
  collectionsPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  collectionSearchResults: [],
  collectionSearchLoading: false,
  collectionSearchError: null,
  collectionSearchPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  importLoading: false,
  importError: null,
  importSuccess: false,
  selectedProducts: [],
  selectedCollections: [],
  formState: {
    loading: false,
    success: false,
    error: null,
  },
};

const wholesalersSlice = createSlice({
  name: "wholesalers",
  initialState,
  reducers: {
    // Fetch Wholesalers
    fetchWholesalersRequest: (
      state,
      _action: PayloadAction<FetchWholesalersOptions>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchWholesalersSuccess: (
      state,
      action: PayloadAction<{
        list: Wholesaler[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.items = action.payload.list;
      state.pagination.page = action.payload.page;
      state.pagination.total = action.payload.total;
      state.pagination.pageSize = action.payload.pageSize;
      state.pagination.totalPages = action.payload.totalPages;
      state.loading = false;
    },
    fetchWholesalersFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Single Wholesaler Operations
    fetchWholesalerRequest: (
      state,
      _action: PayloadAction<{ wholesalerId: string }>,
    ) => {
      state.wholesalerLoading = true;
      state.wholesalerError = null;
    },
    fetchWholesalerSuccess: (
      state,
      action: PayloadAction<{ wholesaler: Wholesaler }>,
    ) => {
      state.currentWholesaler = action.payload.wholesaler;
      state.wholesalerLoading = false;
      state.wholesalerError = null;
    },
    fetchWholesalerFailure: (state, action: PayloadAction<ApiError>) => {
      state.wholesalerLoading = false;
      state.wholesalerError = action.payload;
    },

    // Search Wholesalers
    searchWholesalersRequest: (
      state,
      _action: PayloadAction<SearchWholesalersOptions>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchWholesalersSuccess: (
      state,
      action: PayloadAction<{
        list: Wholesaler[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.searchResults = action.payload.list;
      state.searchPagination.page = action.payload.page;
      state.searchPagination.total = action.payload.total;
      state.searchPagination.pageSize = action.payload.pageSize;
      state.searchPagination.totalPages = action.payload.totalPages;
      state.searchLoading = false;
    },
    searchWholesalersFailure: (state, action: PayloadAction<ApiError>) => {
      state.searchError = action.payload;
      state.searchLoading = false;
    },

    // Wholesaler Products
    fetchWholesalerProductsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        options: FetchWholesalerProductsOptions;
      }>,
    ) => {
      state.productsLoading = true;
      state.productsError = null;
    },
    fetchWholesalerProductsSuccess: (
      state,
      action: PayloadAction<{
        list: WholesalerProduct[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.wholesalerProducts = action.payload.list;
      state.productsPagination.page = action.payload.page;
      state.productsPagination.total = action.payload.total;
      state.productsPagination.pageSize = action.payload.pageSize;
      state.productsPagination.totalPages = action.payload.totalPages;
      state.productsLoading = false;
    },
    fetchWholesalerProductsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.productsError = action.payload;
      state.productsLoading = false;
    },

    // Search Wholesaler Products
    searchWholesalerProductsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        options: SearchWholesalerProductsOptions;
      }>,
    ) => {
      state.productSearchLoading = true;
      state.productSearchError = null;
    },
    searchWholesalerProductsSuccess: (
      state,
      action: PayloadAction<{
        list: WholesalerProduct[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.productSearchResults = action.payload.list;
      state.productSearchPagination.page = action.payload.page;
      state.productSearchPagination.total = action.payload.total;
      state.productSearchPagination.pageSize = action.payload.pageSize;
      state.productSearchPagination.totalPages = action.payload.totalPages;
      state.productSearchLoading = false;
    },
    searchWholesalerProductsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.productSearchError = action.payload;
      state.productSearchLoading = false;
    },

    // Wholesaler Collections
    fetchWholesalerCollectionsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        options: FetchWholesalerCollectionsOptions;
      }>,
    ) => {
      state.collectionsLoading = true;
      state.collectionsError = null;
    },
    fetchWholesalerCollectionsSuccess: (
      state,
      action: PayloadAction<{
        list: WholesalerCollection[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.wholesalerCollections = action.payload.list;
      state.collectionsPagination.page = action.payload.page;
      state.collectionsPagination.total = action.payload.total;
      state.collectionsPagination.pageSize = action.payload.pageSize;
      state.collectionsPagination.totalPages = action.payload.totalPages;
      state.collectionsLoading = false;
    },
    fetchWholesalerCollectionsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.collectionsError = action.payload;
      state.collectionsLoading = false;
    },

    // Search Wholesaler Collections
    searchWholesalerCollectionsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        options: SearchWholesalerCollectionsOptions;
      }>,
    ) => {
      state.collectionSearchLoading = true;
      state.collectionSearchError = null;
    },
    searchWholesalerCollectionsSuccess: (
      state,
      action: PayloadAction<{
        list: WholesalerCollection[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.collectionSearchResults = action.payload.list;
      state.collectionSearchPagination.page = action.payload.page;
      state.collectionSearchPagination.total = action.payload.total;
      state.collectionSearchPagination.pageSize = action.payload.pageSize;
      state.collectionSearchPagination.totalPages = action.payload.totalPages;
      state.collectionSearchLoading = false;
    },
    searchWholesalerCollectionsFailure: (
      state,
      action: PayloadAction<ApiError>,
    ) => {
      state.collectionSearchError = action.payload;
      state.collectionSearchLoading = false;
    },

    // Import Products
    importProductsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        request: ImportProductsRequest;
      }>,
    ) => {
      state.importLoading = true;
      state.importError = null;
      state.importSuccess = false;
    },
    importProductsSuccess: (
      state,
      _action: PayloadAction<{ imported: number; updated: number }>,
    ) => {
      state.importLoading = false;
      state.importSuccess = true;
      // Mark imported products as mapped
      state.wholesalerProducts = state.wholesalerProducts.map((product) => {
        if (state.selectedProducts.includes(product.id)) {
          return { ...product, is_mapped: true };
        }
        return product;
      });
      // Clear selections
      state.selectedProducts = [];
    },
    importProductsFailure: (state, action: PayloadAction<ApiError>) => {
      state.importLoading = false;
      state.importError = action.payload;
      state.importSuccess = false;
    },

    // Import Collections
    importCollectionsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        request: ImportCollectionsRequest;
      }>,
    ) => {
      state.importLoading = true;
      state.importError = null;
      state.importSuccess = false;
    },
    importCollectionsSuccess: (
      state,
      _action: PayloadAction<{ imported: number; updated: number }>,
    ) => {
      state.importLoading = false;
      state.importSuccess = true;
      // Mark imported collections as mapped
      state.wholesalerCollections = state.wholesalerCollections.map(
        (collection) => {
          if (state.selectedCollections.includes(collection.id)) {
            return { ...collection, is_mapped: true };
          }
          return collection;
        },
      );
      // Clear selections
      state.selectedCollections = [];
    },
    importCollectionsFailure: (state, action: PayloadAction<ApiError>) => {
      state.importLoading = false;
      state.importError = action.payload;
      state.importSuccess = false;
    },

    // Unmap Products
    unmapProductsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        request: UnmapProductsRequest;
      }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    unmapProductsSuccess: (
      state,
      _action: PayloadAction<{ unmapped: number }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      // Mark unmapped products
      state.wholesalerProducts = state.wholesalerProducts.map((product) => {
        if (state.selectedProducts.includes(product.id)) {
          return { ...product, is_mapped: false, mapped_product_id: undefined };
        }
        return product;
      });
      // Clear selections
      state.selectedProducts = [];
    },
    unmapProductsFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Unmap Collections
    unmapCollectionsRequest: (
      state,
      _action: PayloadAction<{
        wholesalerId: string;
        request: UnmapCollectionsRequest;
      }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    unmapCollectionsSuccess: (
      state,
      _action: PayloadAction<{ unmapped: number }>,
    ) => {
      state.formState.loading = false;
      state.formState.success = true;
      // Mark unmapped collections
      state.wholesalerCollections = state.wholesalerCollections.map(
        (collection) => {
          if (state.selectedCollections.includes(collection.id)) {
            return {
              ...collection,
              is_mapped: false,
              mapped_collection_id: undefined,
            };
          }
          return collection;
        },
      );
      // Clear selections
      state.selectedCollections = [];
    },
    unmapCollectionsFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Selection Management
    toggleProductSelection: (state, action: PayloadAction<string>) => {
      const productId = action.payload;
      if (state.selectedProducts.includes(productId)) {
        state.selectedProducts = state.selectedProducts.filter(
          (id) => id !== productId,
        );
      } else {
        state.selectedProducts.push(productId);
      }
    },
    toggleCollectionSelection: (state, action: PayloadAction<string>) => {
      const collectionId = action.payload;
      if (state.selectedCollections.includes(collectionId)) {
        state.selectedCollections = state.selectedCollections.filter(
          (id) => id !== collectionId,
        );
      } else {
        state.selectedCollections.push(collectionId);
      }
    },
    selectAllProducts: (state) => {
      state.selectedProducts = state.wholesalerProducts.map(
        (product) => product.id,
      );
    },
    selectAllCollections: (state) => {
      state.selectedCollections = state.wholesalerCollections.map(
        (collection) => collection.id,
      );
    },
    clearProductSelection: (state) => {
      state.selectedProducts = [];
    },
    clearCollectionSelection: (state) => {
      state.selectedCollections = [];
    },

    // Form State Management
    resetFormState: (state) => {
      state.formState.loading = false;
      state.formState.error = null;
      state.formState.success = false;
    },
    resetImportState: (state) => {
      state.importLoading = false;
      state.importError = null;
      state.importSuccess = false;
    },

    // Clear states
    clearCurrentWholesaler: (state) => {
      state.currentWholesaler = null;
      state.wholesalerError = null;
      state.wholesalerProducts = [];
      state.wholesalerCollections = [];
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
    clearProductSearchResults: (state) => {
      state.productSearchResults = [];
      state.productSearchError = null;
    },
    clearCollectionSearchResults: (state) => {
      state.collectionSearchResults = [];
      state.collectionSearchError = null;
    },
  },
});

export const {
  // Fetch wholesalers
  fetchWholesalersRequest,
  fetchWholesalersSuccess,
  fetchWholesalersFailure,

  // Single wholesaler
  fetchWholesalerRequest,
  fetchWholesalerSuccess,
  fetchWholesalerFailure,

  // Search wholesalers
  searchWholesalersRequest,
  searchWholesalersSuccess,
  searchWholesalersFailure,

  // Wholesaler products
  fetchWholesalerProductsRequest,
  fetchWholesalerProductsSuccess,
  fetchWholesalerProductsFailure,
  searchWholesalerProductsRequest,
  searchWholesalerProductsSuccess,
  searchWholesalerProductsFailure,

  // Wholesaler collections
  fetchWholesalerCollectionsRequest,
  fetchWholesalerCollectionsSuccess,
  fetchWholesalerCollectionsFailure,
  searchWholesalerCollectionsRequest,
  searchWholesalerCollectionsSuccess,
  searchWholesalerCollectionsFailure,

  // Import operations
  importProductsRequest,
  importProductsSuccess,
  importProductsFailure,
  importCollectionsRequest,
  importCollectionsSuccess,
  importCollectionsFailure,

  // Unmap operations
  unmapProductsRequest,
  unmapProductsSuccess,
  unmapProductsFailure,
  unmapCollectionsRequest,
  unmapCollectionsSuccess,
  unmapCollectionsFailure,

  // Selection management
  toggleProductSelection,
  toggleCollectionSelection,
  selectAllProducts,
  selectAllCollections,
  clearProductSelection,
  clearCollectionSelection,

  // Utilities
  resetFormState,
  resetImportState,
  clearCurrentWholesaler,
  clearSearchResults,
  clearProductSearchResults,
  clearCollectionSearchResults,
} = wholesalersSlice.actions;

export default wholesalersSlice.reducer;
